/* eslint-disable react/destructuring-assignment */
/**
 * This is a container for all route handlers. It can be simple and
 * dumb as is (passes route params down to RouteHandler),
 * but allows for an extra layer of control over all nested routes in needed.
 */

import React, { useEffect, useMemo } from 'react';
import _ from 'lodash';
import { Tracking as nwTracking } from '@nerdwallet/nw-client-lib';
import { useServerRequest } from '@nerdwallet/coin/SSRContext';

import { DisplayPageContextProvider } from '@nerdwallet/structured-content/contexts/DisplayPageContext';
import { MarketplaceContextProvider } from '@nerdwallet/structured-content/contexts/MarketplaceContext';
import { MarketplaceProductCardContextProvider } from '@nerdwallet/structured-content/contexts/MarketplaceProductCardContext';
import { AnchorContextProvider } from '@nerdwallet/structured-content/contexts/AnchorContext';
import { CitationContextProvider } from '@nerdwallet/structured-content/contexts/CitationContext';
import { BANKING_DISPLAY_PAGES } from '@nerdwallet/marketplace/constants';
import { Box } from '@nerdwallet/currency';
import { StructuredContentProvider } from '@nerdwallet/structured-content';

import AppRoutes from './app-routes';
import { ReactRouterChildrenProps } from '~/lib/types';
import { createStructuredContentClient } from '~/lib/structured-content';

const SC_CLIENT_PATH = 'res.locals.structuredContentClient';

const Container: React.FC<ReactRouterChildrenProps> = (props) => {
  const serverReq = useServerRequest();

  const structuredContentClient = useMemo(() => {
    // Attempt to retrieve the client from the serverRequest
    if (_.get(serverReq, SC_CLIENT_PATH)) {
      return _.get(serverReq, SC_CLIENT_PATH);
    }

    const client = createStructuredContentClient(serverReq);
    _.set(serverReq, SC_CLIENT_PATH, client);
    return client;
  }, [serverReq]);

  useEffect(() => {
    // Log the page view when the route changes.
    // TODO: Move the error handling on nw-client-lib.
    nwTracking.logPageView(props.location.pathname);
  }, [props.location.pathname]);

  return (
    <StructuredContentProvider client={structuredContentClient}>
      <DisplayPageContextProvider displayPage={BANKING_DISPLAY_PAGES.CONTENT}>
        <MarketplaceContextProvider>
          <AnchorContextProvider>
            <MarketplaceProductCardContextProvider
              props={{ isKetoCard: true }}
              ketoProps={{ forceMobile: true }}
            >
              <CitationContextProvider>
                <Box className="route-container">
                  <AppRoutes {...props} />
                </Box>
              </CitationContextProvider>
            </MarketplaceProductCardContextProvider>
          </AnchorContextProvider>
        </MarketplaceContextProvider>
      </DisplayPageContextProvider>
    </StructuredContentProvider>
  );
};

export default Container;
